/**
 * speech.js (TTS defect fix, 2026-08-26)
 *
 * How the OPERATOR voice says figures and names, and what the transcriber is
 * primed to hear. Two exports, both pure, both covered by speech.test.mjs:
 *
 * - OPERATOR_SPEECH_RULES: the prompt block appended to the operator system
 *   prompt. Mirrors the composer's rule 20 ("UNITS AND FIGURES IN SPEECH",
 *   ora-core orbi-context-fetch) for the staff console: the operator heard
 *   "842" as "eight forty two" and "336" as "three thirty six" because a bare
 *   numeral is a string the speech engine has to decide the MEANING of, and it
 *   decides wrong in the direction of a street address. Words cannot be
 *   misread, so anything that must land correctly goes out as words.
 *
 * - buildDockKeyterms: the transcriber priming list, built from proper nouns
 *   already on screen (client names, outage area labels, the open subscriber),
 *   mirroring the phone rail's buildKeyterms (ora-core _shared/caller-name.ts:
 *   cities stay in the keyterm list so the transcriber still hears them). The
 *   dock rail never had one, which is how "Wichita Falls" transcribed as
 *   "Witcher Falls". Same budget discipline: capped terms and tokens, well
 *   under Deepgram's ceiling. keyterm requires nova-3; the caller pairs this
 *   list with that model.
 *
 * Plain JS (allowJs, same as generativeUi.js) so the node test battery runs it
 * without a TS toolchain. No em dashes; no internal codename in any string.
 */

/**
 * Prompt block for the operator voice: how to speak figures and names.
 * Appended to the operator system prompt at call start.
 */
export const OPERATOR_SPEECH_RULES =
  ' SPOKEN FIGURES AND NAMES (you speak through a voice engine; how you WRITE a value decides how it is HEARD): ' +
  'A COUNT or quantity (customers, tickets, connections down, bookings) is ONE natural cardinal number spoken in words: 842 is "eight hundred and forty two", 336 is "three hundred and thirty six". Never the address-style split ("eight forty two", "three thirty six") and never digit by digit. ' +
  'An ACCOUNT NUMBER, ticket number, or reference code is NOT a quantity: read it digit by digit, exactly as returned, every time. ' +
  'A dollar figure is spoken in words with the unit: $1,240 is "one thousand two hundred and forty dollars". ' +
  'A PLACE, area, or building name is said exactly as it is written on screen; if you are not sure which place the operator named, ask, never substitute a similar-sounding name.';

/* Budget mirror of the phone rail (caller-name.ts): Deepgram's keyterm ceiling
   is 500 tokens; stay well under it. */
const MAX_TERMS = 60;
const MAX_TOKENS = 400;

/**
 * Build the transcriber keyterm list from proper nouns already in hand.
 * Accepts anything; keeps trimmed, deduped (case-insensitive), sane-length
 * strings, capped by term count and total whitespace tokens.
 * @param {Array<unknown>} parts candidate names (client names, outage area
 *   labels, the open subscriber's name); non-strings and blanks are dropped.
 * @returns {string[]}
 */
export function buildDockKeyterms(parts) {
  const out = [];
  const seen = new Set();
  let tokens = 0;
  for (const raw of Array.isArray(parts) ? parts : []) {
    if (typeof raw !== 'string') continue;
    const term = raw.replace(/\s+/g, ' ').trim();
    if (term.length < 3 || term.length > 40) continue;
    const key = term.toLowerCase();
    if (seen.has(key)) continue;
    const cost = term.split(' ').length;
    if (out.length >= MAX_TERMS || tokens + cost > MAX_TOKENS) break;
    seen.add(key);
    out.push(term);
    tokens += cost;
  }
  return out;
}
